import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  ArrowLeft,
  User,
  FileText,
  Folder,
  MessageSquare,
  MapPin,
  GraduationCap,
  Users,
  Briefcase,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';

import AdminStudentPersonalForm from '../../components/admin/AdminStudentPersonalForm';
import AdminStudentServicesTab from '../../components/admin/AdminStudentServicesTab';
import AdminStudentDocumentsTab from '../../components/admin/AdminStudentDocumentsTab';
import AdminStudentNotesTab from '../../components/admin/AdminStudentNotesTab';
import AdminStudentRelationshipsTab from '../../components/admin/AdminStudentRelationshipsTab';

export default function AdminStudentDetails() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('personal');
  const [counselors, setCounselors] = useState([]);
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    if (studentId) {
      fetchStudentDetails();
      fetchCounselorsAndAgents();
    }
  }, [studentId]);

  const fetchStudentDetails = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getStudentById(studentId);
      setStudent(response.student || response);
    } catch (error) {
      console.error('Failed to fetch student details:', error);
      toast.error('Failed to load student details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCounselorsAndAgents = async () => {
    try {
      const [counselorsRes, agentsRes] = await Promise.all([
        adminAPI.getCounselors().catch(() => ({ counselors: [] })),
        adminAPI.getAgents().catch(() => ({ agents: [] })),
      ]);
      setCounselors(counselorsRes.counselors || []);
      setAgents(agentsRes.agents || []);
    } catch (error) {
      console.error('Failed to fetch counselors/agents:', error);
    }
  };

  const handleUpdate = () => {
    fetchStudentDetails();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!student) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
          <User className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Student Not Found
          </h2>
          <p className="text-gray-500 mb-4">
            The student you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate('/admin/students')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Students
          </Button>
        </div>
      </AdminLayout>
    );
  }

  // Normalize student data
  const studentName = student.firstName || student.user?.firstName || 'Unknown';
  const studentLastName = student.lastName || student.user?.lastName || '';
  const studentEmail = student.email || student.user?.email || '';
  const studentInitials =
    `${studentName[0] || ''}${studentLastName[0] || ''}`.toUpperCase();

  // Calculate service metrics
  const serviceRequests = student.serviceRequests || [];
  const serviceMetrics = {
    applied: serviceRequests.length,
    inProgress: serviceRequests.filter(sr => sr.status === 'IN_PROGRESS')
      .length,
    completed: serviceRequests.filter(sr => sr.status === 'COMPLETED').length,
  };

  // Count documents
  const documents = student.documents || {};
  const documentCount = Object.values(documents).filter(Boolean).length;

  // Get latest note
  const latestNote = student.latestNote?.text || null;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900">
              Student Profile
            </h1>
            <p className="text-gray-600">
              Manage student information, services, and assignments
            </p>
          </div>
        </div>

        {/* Main Content - 30/70 Split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Sidebar - 30% */}
          <div className="lg:col-span-4 ">
            <Card className="p-6 sticky top-6">
              {/* Avatar & Basic Info */}
              <div className="text-center mb-6">
                <Avatar className="h-24 w-24 mx-auto mb-4">
                  <AvatarFallback className="text-2xl font-bold text-white bg-gradient-to-br from-[#F2575F] to-[#E04850]">
                    {studentInitials}
                  </AvatarFallback>
                </Avatar>
                <h3 className="text-xl font-semibold text-gray-900">
                  {studentName} {studentLastName}
                </h3>
                <p className="text-sm text-gray-500">{studentEmail}</p>
                <Badge
                  className={`mt-2 ${student.onboardingCompleted ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}
                >
                  {student.onboardingCompleted ? 'Active' : 'Onboarding'}
                </Badge>
              </div>

              {/* Quick Stats */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                  <GraduationCap className="w-5 h-5 text-purple-500" />
                  <div>
                    <p className="text-xs text-gray-500">Institution</p>
                    <p className="text-sm font-medium text-gray-900">
                      {student.institution || 'Not specified'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="text-xs text-gray-500">Preferred Country</p>
                    <p className="text-sm font-medium text-gray-900">
                      {student.interestedCountries?.join(', ') ||
                        student.preferredDestination ||
                        'Not specified'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                  <Folder className="w-5 h-5 text-amber-500" />
                  <div>
                    <p className="text-xs text-gray-500">Documents</p>
                    <p className="text-sm font-medium text-gray-900">
                      {documentCount} uploaded
                    </p>
                  </div>
                </div>
              </div>

              {/* Service Metrics */}
              <div className="grid grid-cols-3 gap-2 mb-6">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">
                    {serviceMetrics.applied}
                  </p>
                  <p className="text-xs text-blue-600">Applied</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">
                    {serviceMetrics.inProgress}
                  </p>
                  <p className="text-xs text-amber-600">In Progress</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">
                    {serviceMetrics.completed}
                  </p>
                  <p className="text-xs text-green-600">Completed</p>
                </div>
              </div>

              {/* Latest Note */}
              {latestNote && (
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <p className="text-xs text-purple-600 font-medium mb-1">
                    Latest Remark
                  </p>
                  <p className="text-sm text-gray-700 line-clamp-3">
                    {latestNote}
                  </p>
                </div>
              )}
            </Card>
          </div>

          {/* Right Content - 70% */}
          <div className="lg:col-span-8 ">
            <Card className="p-6">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-5 mb-6">
                  <TabsTrigger value="personal" className="gap-2">
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">Personal</span>
                  </TabsTrigger>
                  <TabsTrigger value="services" className="gap-2">
                    <Briefcase className="w-4 h-4" />
                    <span className="hidden sm:inline">Services</span>
                  </TabsTrigger>
                  <TabsTrigger value="documents" className="gap-2">
                    <FileText className="w-4 h-4" />
                    <span className="hidden sm:inline">Documents</span>
                  </TabsTrigger>
                  <TabsTrigger value="relationships" className="gap-2">
                    <Users className="w-4 h-4" />
                    <span className="hidden sm:inline">Service Teams</span>
                  </TabsTrigger>
                  <TabsTrigger value="notes" className="gap-2">
                    <MessageSquare className="w-4 h-4" />
                    <span className="hidden sm:inline">Notes</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="personal">
                  <AdminStudentPersonalForm
                    student={student}
                    onUpdate={handleUpdate}
                  />
                </TabsContent>

                <TabsContent value="services">
                  <AdminStudentServicesTab
                    studentId={studentId}
                    serviceRequests={serviceRequests}
                    counselors={counselors}
                    agents={agents}
                    onUpdate={handleUpdate}
                  />
                </TabsContent>

                <TabsContent value="documents">
                  <AdminStudentDocumentsTab
                    studentId={studentId}
                    documents={documents}
                    onUpdate={handleUpdate}
                  />
                </TabsContent>

                <TabsContent value="relationships">
                  <AdminStudentRelationshipsTab
                    student={student}
                    counselors={counselors}
                    agents={agents}
                    onUpdate={handleUpdate}
                  />
                </TabsContent>

                <TabsContent value="notes">
                  <AdminStudentNotesTab studentId={studentId} />
                </TabsContent>
              </Tabs>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
